import { graphql } from 'gatsby';
import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ALL_LANGS } from '../constants';
import { LocationContext } from '../contexts/LocationContext';
import { getPortableText, getPortableTextAsString, localize } from '../utils/sanity';
import { buildFundSchema, getImageUrl, JsonLd } from '../utils/structuredData';

import BlockContent from '@sanity/block-content-to-react';
import { FaExclamationCircle } from 'react-icons/fa';
import PageSEO from '../components/PageSEO';
import CharacteristicsSection from '../components/ui/CharacteristicsSection';
import Classification from '../components/ui/Classification';
import Hero from '../components/ui/Hero';
import Image from '../components/ui/Image';
import ModalOverlay from '../components/ui/ModalOverlay';
import SanityLayout from '../components/ui/SanityLayout';
import FormModule from '../components/ui/modules/FormModule';
import TextAndImageModule from '../components/ui/modules/TextAndImageModule';
import TextWithBackgroundImageModule from '../components/ui/modules/TextWithBackgroundImageModule';
import { SanityImageType } from '../graphql-fragments/SanityImage';
import { LocalizedSEO } from '../graphql-fragments/seo';
import APFIPPLogo from '../images/APFIPP_logo.png';
import morningstar from '../images/Morningstar_Logo.svg';
import serializers from '../serializers';
import { RawPortableText } from '../types/types';
import { withI18n } from '../utils/hocs';
import { clsx } from '../utils/utils';
import * as styles from './MutualFund.module.scss';

export interface MutualFund {
  __typename: 'SanitySecuritiesFund';
  image: SanityImageType;
  slug: {
    current: string;
  };
  videoUrl?: string;
  name: string;
  isGoldenVisaElegible?: boolean;
  shortDescription: string;
  descriptionSection?: {
    title?: string;
    text?: RawPortableText;
    image: SanityImageType;
  };
  investmentRisk?: {
    investmentRisk: string;
    description: string;
  };
  valuesUpdateInfo?: {
    updateFrequencyText: string;
    lastUpdateDate: string;
  };
  metrics?: {
    nav: string;
    YTD: number;
    twelveM: number;
  };
  characteristics: Array<{
    title: string;
    additionalExplanation?: string;
    description: RawPortableText;
  }>;
  assetTypes?: string;
  top5Holdings?: Array<{
    symbol: string;
    company: string;
    assets: number;
  }>;
  top5Issuers?: {
    companyLabel: string;
    issuers: Array<{
      company: string;
    }>;
  };
  documents?: Array<{
    name: string;
    description: string;
    file?: {
      asset: {
        url: string;
        size: number;
      };
    };
    link?: string;
  }>;
  fundRating?: {
    threeYearsRating?: '1' | '2' | '3' | '4' | '5' | 'hidden';
    fiveYearsRating?: '1' | '2' | '3' | '4' | '5' | 'hidden';
    tenYearsRating?: '1' | '2' | '3' | '4' | '5' | 'hidden';
    overallRating?: '1' | '2' | '3' | '4' | '5' | 'hidden';
  };
  APFIPPprizes?: Array<string>;
  otherPrizes?: Array<{
    name: string;
    image: SanityImageType;
  }>;
  seo: LocalizedSEO;
}

interface LocalizedQueryData {
  sanityMutualFund: MutualFund;
  sanityMutualFundsSettings: {
    disclaimer?: RawPortableText;
    documentsSectionImage: SanityImageType;
    prizesSectionIntroText?: string;
    prizesSectionMobileImage: SanityImageType;
    prizesSectionDesktopImage: SanityImageType;
  };
}

export const query = graphql`
  query GetMutualFundById($id: String!) {
    sanityMutualFund: sanitySecuritiesFund(_id: { eq: $id }) {
      ...MutualFund
    }
    sanityMutualFundsSettings {
      disclaimer {
        ...LocaleFundCharacteristicPortableText
      }
      documentsSectionImage {
        ...SanityImage
      }
      prizesSectionIntroText {
        ...LocaleTextFragment
      }
      prizesSectionMobileImage {
        ...SanityImage
      }
      prizesSectionDesktopImage {
        ...SanityImage
      }
    }
  }
`;

interface MutualFundProps {
  data: any;
}

const MutualFund = ({ data }: MutualFundProps): React.ReactElement => {
  const { i18n, t } = useTranslation();
  const langs = [i18n.language, ...ALL_LANGS.filter(lang => lang !== i18n.language)];
  const localizedData: LocalizedQueryData = localize(data, langs);

  const {
    image,
    videoUrl,
    name,
    shortDescription,
    descriptionSection,
    investmentRisk,
    valuesUpdateInfo,
    metrics,
    characteristics,
    documents,
    fundRating,
    APFIPPprizes,
    otherPrizes,
    assetTypes,
    isGoldenVisaElegible,

    seo,
  } = localizedData.sanityMutualFund;

  const {
    disclaimer,
    documentsSectionImage,
    prizesSectionIntroText,
    prizesSectionMobileImage,
    prizesSectionDesktopImage,
  } = localizedData.sanityMutualFundsSettings;

  const [modalOpen, setModalOpen] = useState(false);

  const hasFundRating =
    fundRating &&
    (fundRating.threeYearsRating !== 'hidden' ||
      fundRating.fiveYearsRating !== 'hidden' ||
      fundRating.tenYearsRating !== 'hidden' ||
      fundRating.overallRating !== 'hidden');

  const hasOtherPrizes = otherPrizes && otherPrizes.length > 0;
  const hasAPFIPPprizes = APFIPPprizes && APFIPPprizes.length > 0;

  const investmentRiskToBarsClass = {
    1: styles.oneBarFill,
    2: styles.twoBarFill,
    3: styles.threeBarFill,
    4: styles.fourBarFill,
    5: styles.fiveBarFill,
    6: styles.sixBarFill,
    7: styles.sevenBarFill,
  };

  const heroRef = useRef<HTMLElement | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const location = useContext(LocationContext)!;

  // Morningstar star ratings, skipping any marked "hidden".
  const fundRatingSchemaEntries = fundRating
    ? [
        { label: 'Morningstar Rating (3 years)', value: fundRating.threeYearsRating },
        { label: 'Morningstar Rating (5 years)', value: fundRating.fiveYearsRating },
        { label: 'Morningstar Rating (10 years)', value: fundRating.tenYearsRating },
        { label: 'Morningstar Rating (overall)', value: fundRating.overallRating },
      ]
        .filter(r => !!r.value && r.value !== 'hidden')
        .map(r => ({ label: r.label, value: parseInt(r.value as string, 10) }))
    : [];

  const fundAwards = [...(APFIPPprizes || []), ...(otherPrizes || []).map(prize => prize.name)];

  const fundCharacteristics = (characteristics || []).map(characteristic => ({
    name: characteristic.title,
    value: getPortableTextAsString(characteristic.description),
  }));

  const prizesTextWithoutBackground =
    !prizesSectionIntroText && !hasAPFIPPprizes && !hasOtherPrizes;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };
  return (
    <SanityLayout heroRef={heroRef}>
      <PageSEO pageSEO={seo} defaultTitle={name} defaultDescription={shortDescription} />
      <JsonLd
        data={buildFundSchema({
          name,
          description: shortDescription,
          imageUrl: getImageUrl(image),
          category: assetTypes,
          goldenVisaEligible: isGoldenVisaElegible,
          awards: fundAwards,
          ratings: fundRatingSchemaEntries,
          characteristics: fundCharacteristics,
          pathname: location.pathname,
        })}
      />
      <Hero
        heroRef={heroRef}
        title={name}
        text={shortDescription}
        textSide="left"
        mobileBackgroundImage={image}
        desktopBackgroundImage={image}
        backgroundType="image"
        withSideVideo={!!videoUrl}
        videoUrl={videoUrl}
      >
        {valuesUpdateInfo && (
          <div className={styles.valuesUpdateInfo}>
            <span className={styles.valuesUpdateText}>{valuesUpdateInfo.updateFrequencyText}</span>
            {valuesUpdateInfo.lastUpdateDate && (
              <p className={styles.valuesUpdateDate}>
                <span>{t('mutualFund.last_update', 'Última atualização')}: </span>
                {formatDate(valuesUpdateInfo.lastUpdateDate)}
              </p>
            )}
          </div>
        )}
        {metrics && (
          <div className={styles.metricsContainer}>
            <div className={styles.metricsWrapper}>
              <span className={styles.metric} title="Net Asset Value">
                <strong>NAV: </strong>
                {metrics.nav}
              </span>
              {metrics.YTD && (
                <span className={styles.metric} title="Year to Date">
                  <strong>YTD: </strong>
                  {metrics.YTD + '%'}
                </span>
              )}
              {metrics.twelveM && (
                <span className={styles.metric}>
                  <strong>12M: </strong>
                  {metrics.twelveM + '%'}
                </span>
              )}
            </div>
            {investmentRisk && (
              <div className={styles.investmentRiskSection}>
                <h4 className={styles.investmentRiskTitle}>
                  {t('investmentRisk.title', 'Risco do Investimento')}
                </h4>
                <div
                  className={
                    styles.investmentRiskBarsContainer +
                    ' ' +
                    investmentRiskToBarsClass[investmentRisk.investmentRisk]
                  }
                >
                  <div className={styles.bar}></div>
                  <div className={styles.bar}></div>
                  <div className={styles.bar}></div>
                  <div className={styles.bar}></div>
                  <div className={styles.bar}></div>
                  <div className={styles.bar}></div>
                  <div className={styles.bar}></div>
                </div>
                <div className={styles.investmentRiskLabels}>
                  <span>{t('investmentRisk-low.title', 'Baixo')}</span>
                  <span>{t('investmentRisk-high.title', 'Alto')}</span>
                </div>
              </div>
            )}
            {investmentRisk?.description && (
              <p className={styles.description}>{investmentRisk.description}</p>
            )}
            {disclaimer && (
              <div className={styles.disclaimer}>
                <button
                  className={styles.disclaimerLink}
                  onClick={() => {
                    setModalOpen(true);
                  }}
                >
                  <FaExclamationCircle />
                  {t('securitiesFundDisclaimer.title', 'Menções obrigatórias')}
                </button>
                <ModalOverlay
                  open={modalOpen}
                  onClose={() => {
                    setModalOpen(false);
                  }}
                  title={
                    <div className={styles.disclaimerModalContainer}>
                      <h2 className={styles.disclaimerModalTitle}>
                        {t('securitiesFundDisclaimer.title', 'Menções obrigatórias')}
                      </h2>
                    </div>
                  }
                >
                  <BlockContent
                    className={styles.disclaimerModalText}
                    blocks={disclaimer}
                    renderContainerOnSingleChild
                    serializers={serializers}
                  />
                </ModalOverlay>
              </div>
            )}
          </div>
        )}
      </Hero>
      {descriptionSection && (
        <TextAndImageModule
          image={descriptionSection.image}
          title={descriptionSection.title}
          text={descriptionSection.text}
          backgroundColor="#EFECFA"
          imageOrientation="landscape"
          imageSide="left"
        />
      )}
      <CharacteristicsSection
        title={t('fundCharacteristic.title', 'Características principais')}
        characteristics={characteristics}
      />
      {documents && documents.length > 0 && (
        <TextAndImageModule
          image={documentsSectionImage}
          title={t('securitiesFundDocuments.title', 'Documentos')}
          backgroundColor="#E5F8E8"
          imageOrientation="portrait"
          imageSide="right"
          fileRows={documents}
        />
      )}
      {(hasFundRating || hasOtherPrizes || hasAPFIPPprizes) && (
        <TextWithBackgroundImageModule
          desktopImage={prizesSectionDesktopImage}
          mobileImage={prizesSectionMobileImage}
          title={t('prizeSection.title', 'Premiação do Fundo')}
          text={prizesSectionIntroText ? getPortableText([prizesSectionIntroText]) : undefined}
          textBackgroundColor="#EFECFA"
          textContainerWithoutBackground={prizesTextWithoutBackground}
          backgoundChildren={
            hasFundRating && (
              <div
                className={clsx(
                  styles.ratingAndMorningstarContainer,
                  prizesTextWithoutBackground && styles.prizesTextWithoutBackground,
                )}
              >
                <Classification
                  withSpaceAround={prizesTextWithoutBackground}
                  className={styles.ratingContainer}
                  ratings={[
                    {
                      title: t('mutualFund.rating_title.3_years', '3 anos'),
                      fundRating: fundRating.threeYearsRating,
                    },
                    {
                      title: t('mutualFund.rating_title.5_years', '5 anos'),
                      fundRating: fundRating.fiveYearsRating,
                    },
                    {
                      title: t('mutualFund.rating_title.10_years', '10 anos'),
                      fundRating: fundRating.tenYearsRating,
                    },
                    {
                      title: t('mutualFund.rating_title.overall', 'Geral'),
                      fundRating: fundRating.overallRating,
                    },
                  ]
                    .filter(rating => rating.fundRating && rating.fundRating !== 'hidden')
                    .map(rating => ({
                      ...rating,
                      fundRating: parseInt(rating.fundRating!) as 1 | 2 | 3 | 4 | 5,
                    }))}
                />
                <div className={styles.competentAuthorityContainer}>
                  <h4 className={styles.competentAuthorityTitle}>
                    {t('mutualFund.rating.competent_authority_title', 'Autoridade Competente')}
                  </h4>
                  <div className={styles.morningstarImageContainer}>
                    <img src={morningstar} alt="" />
                  </div>
                </div>
              </div>
            )
          }
        >
          {hasAPFIPPprizes && (
            <div className={styles.APFIPPprizesContainer}>
              <div className={styles.prizeLogoContainer}>
                <img src={APFIPPLogo} alt="APFIPP logo" />
              </div>
              <div className={styles.APFIPPprizesList}>
                <h4 className={styles.prizesTitle}>
                  {t('prizeSection.APFIPP_prizes_title', 'Prémios APFIPP')}
                </h4>
                <div className={styles.APFIPPprizes}>
                  {APFIPPprizes.map((prize, i) => (
                    <span key={i}>{prize}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
          {hasOtherPrizes && (
            <div className={styles.otherPrizesContainer}>
              <h4 className={styles.prizesTitle}>
                {t('prizeSection.other_prizes_title', 'Outros Prémios')}
              </h4>
              <div className={styles.otherPrizesImagesContainer}>
                {otherPrizes.map((prize, i) => (
                  <div className={styles.otherPrize} key={i}>
                    <Image image={prize.image} title={prize.name} alt={prize.name} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </TextWithBackgroundImageModule>
      )}
      <FormModule />
    </SanityLayout>
  );
};

export default withI18n(MutualFund);
